import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import MasterLookupService from '../../../../services/master-lookup.service';
import { Modules, AuthRoutes } from '../../../../enums';

interface CurrencyFormData {
  currency_code: string;
  currency_name: string;
  symbol: string;
  decimal_places: number | string;
  status: 'active' | 'inactive';
  notes: string;
}

const initialForm: CurrencyFormData = {
  currency_code: '',
  currency_name: '',
  symbol: '',
  decimal_places: 2,
  status: 'active',
  notes: '',
};

const CurrencyForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<CurrencyFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    MasterLookupService.get('currencies', id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          currency_code: row.currency_code ?? '',
          currency_name: row.currency_name ?? '',
          symbol: row.symbol ?? '',
          decimal_places: row.decimal_places ?? 2,
          status: row.status ?? 'active',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: CurrencyFormData) => {
      const nextErrors: Record<string, string> = {};
      if (!data.currency_code.trim()) nextErrors.currency_code = t('masters.currencies.form.pageTexts.fields.currencyCode.errors.required');
      if (!data.currency_name.trim()) nextErrors.currency_name = t('masters.currencies.form.pageTexts.fields.currencyName.errors.required');
      if (!data.symbol.trim()) nextErrors.symbol = t('masters.currencies.form.pageTexts.fields.symbol.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'status' ? (e.target.checked ? 'active' : 'inactive') : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = { ...formData, decimal_places: Number(formData.decimal_places) || 0 };

        if (edit && id) {
          await MasterLookupService.update('currencies', id, payload);
          toast.success(<Toast message={t('masters.currencies.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await MasterLookupService.create('currencies', payload);
          toast.success(<Toast message={t('masters.currencies.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.currencies}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.currencies.form.pageTexts.editTitle') : t('masters.currencies.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-3">
          <Input
            id="currency_code"
            name="currency_code"
            label={t('masters.currencies.form.pageTexts.fields.currencyCode.label')}
            placeholder={t('masters.currencies.form.pageTexts.fields.currencyCode.placeholder')}
            required
            value={formData.currency_code}
            error={!!errors.currency_code}
            errorMessage={errors.currency_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-3">
          <Input
            id="currency_name"
            name="currency_name"
            label={t('masters.currencies.form.pageTexts.fields.currencyName.label')}
            placeholder={t('masters.currencies.form.pageTexts.fields.currencyName.placeholder')}
            required
            value={formData.currency_name}
            error={!!errors.currency_name}
            errorMessage={errors.currency_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <Input
            id="symbol"
            name="symbol"
            label={t('masters.currencies.form.pageTexts.fields.symbol.label')}
            placeholder={t('masters.currencies.form.pageTexts.fields.symbol.placeholder')}
            required
            value={formData.symbol}
            error={!!errors.symbol}
            errorMessage={errors.symbol}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <Input
            id="decimal_places"
            name="decimal_places"
            type="number"
            min="0"
            label={t('masters.currencies.form.pageTexts.fields.decimalPlaces.label')}
            placeholder={t('masters.currencies.form.pageTexts.fields.decimalPlaces.placeholder')}
            value={formData.decimal_places}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <CustomizedSwitch
            label={t('masters.currencies.form.pageTexts.fields.status.label')}
            name="status"
            checked={formData.status === 'active'}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.currencies.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.currencies.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.currencies.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.currencies}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.currencies.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default CurrencyForm;
